import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, Empty } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faPlus } from '@fortawesome/pro-light-svg-icons';
import { faArrowRight, faEquals } from '@fortawesome/pro-solid-svg-icons';
import { sortBy } from 'lodash';

import { getScoreColor } from 'utils/ranking';
import { getItemAlteredByScenario } from 'utils/rules';

import './IndicatorsBSC.sass';

// Palette de zones (cyclée, 3-6 catégories) : couleurs bien distinctes sur le
// fond navy, dans la famille de l'app (teal brand + ambre scénarios). Dégradé
// vif en haut de carte -> full transparent (langage zones de la viz risques).
const zone = (r: number, g: number, b: number) => ({
	dot: `rgb(${r},${g},${b})`,
	// 135deg = du haut-gauche (max) vers le bas-droite (transparent).
	gradient: `linear-gradient(225deg, rgba(${r},${g},${b},1) 0%, rgba(${r},${g},${b},0.25) 35%, rgba(${r},${g},${b},0) 85%)`
});
const ZONE_STYLES = [
	// Teintes franches et distinctes (pas de rose) : teal, bleu, ambre,
	// lavande, vert, corail.
	zone(36, 204, 184),   // teal (brand)
	zone(91, 141, 239),   // bleu
	zone(255, 180, 84),   // ambre
	zone(167, 139, 250),  // lavande
	zone(143, 212, 96),   // vert
	zone(255, 122, 89)    // corail
];

type IndicatorsBSCProps = {
	// Indicateurs BRUTS (non altérés) — on applique le scénario nous-mêmes.
	indicators: any[];
	indicatorCategories: any[];
	// Scénario actif (null => baseline) + tous les scénarios (pour la coupe).
	scenario?: any | null;
	scenarios?: any[];
	// Sélectionne / désélectionne un indicateur (side menu).
	select?: (item: any) => void;
	deselect?: () => void;
	selection?: any;
	// Ouvre la modale d'édition (double-clic sur la ligne).
	onEdit?: (item: any) => void;
	// Crée un indicateur pré-catégorisé (bouton + du cadran).
	onCreate?: (indicatorCategoryId: string | null) => void;
	// Met à jour un indicateur (drag&drop entre quadrants).
	onUpdateIndicator?: (id: string, values: any) => void;
};

/**
 * Visualisation « Balanced Scorecard » des indicateurs, dans le langage des
 * zones de la viz risques : un cadran-zone par catégorie (titre ● UPPERCASE,
 * fond dégradé discret), disposés au mieux (3 -> 3 colonnes, 4 -> 2x2,
 * 5/6 -> 3x2). Chaque indicateur est un MINI-AXE 0-100 : pastille couleur-gauge
 * positionnée à sa valeur, marqueur baseline en scénario, flèche d'évolution et
 * coupe (meilleure valeur across scénarios). Clic -> popover slider. Pierre.
 */
export const IndicatorsBSC = ({
	indicators = [],
	indicatorCategories = [],
	scenario = null,
	scenarios = [],
	select,
	deselect,
	selection,
	onEdit,
	onCreate,
	onUpdateIndicator
}: IndicatorsBSCProps) => {
	const { t } = useTranslation();
	const [draggingId, setDraggingId] = React.useState<string | null>(null);
	const [dropTargetId, setDropTargetId] = React.useState<string | null>(null);

	// Scores par indicateur et par scénario (baseline incluse) pour les flèches
	// (vs baseline) et la coupe (meilleure valeur across scénarios).
	const parseTrend = (v: any) => {
		const n = parseFloat(v);
		return Number.isNaN(n) ? 50 : n;
	};
	const allScenarios: any[] = [null, ...(scenarios || [])];
	const scores: Record<string, Record<string, number>> = {};
	indicators.forEach((i) => {
		scores[i.id] = {};
		allScenarios.forEach((sc) => {
			scores[i.id][sc?.id || 'baseline'] = parseTrend(
				getItemAlteredByScenario(i, sc)?.trend
			);
		});
	});
	const maxScore = (id: string) => Math.max(...Object.values(scores[id] || { 0: 0 }));
	const minScore = (id: string) => Math.min(...Object.values(scores[id] || { 0: 0 }));

	// Indicateurs altérés par le scénario actif, actifs seulement.
	const altered = indicators
		.map((i) => ({ raw: i, alt: getItemAlteredByScenario(i, scenario) }))
		.filter((e) => e.alt && e.alt.enabled !== false);

	// Cartes : catégories triées par nom + « Aucune catégorie » si nécessaire.
	// Ordre BSC canonique = ordre de création (Finance, Customer, Processes,
	// Learning & Growth pour les défauts/backfill) — pas l'alphabétique.
	const cards: Array<{ id: string | null; name: string; items: typeof altered }> =
		sortBy(indicatorCategories, [(c: any) => c.createdAt || '', 'name']).map((c: any) => ({
			id: c.id,
			name: c.name,
			items: altered.filter((e) => e.raw.indicatorCategoryId === c.id)
		}));
	const uncategorized = altered.filter(
		(e) => !e.raw.indicatorCategoryId || !indicatorCategories.find((c: any) => c.id === e.raw.indicatorCategoryId)
	);
	if (uncategorized.length > 0) {
		cards.push({
			id: null,
			name: t('models.indicator_category.messages.no_x'),
			items: uncategorized
		});
	}

	if (cards.length === 0) {
		return <Empty description={false} style={{ padding: '4rem 0' }} />;
	}

	// Disposition au mieux : 3 -> 3 colonnes, 4 -> 2x2, 5/6 -> 3x2.
	const n = cards.length;
	const cols = n <= 3 ? Math.max(n, 1) : n === 4 ? 2 : 3;

	return (
		<div
			className="IndicatorsBSC"
			data-testid="indicators-bsc"
			style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
		>
			{cards.map((card, cardIndex) => {
				const zone = ZONE_STYLES[cardIndex % ZONE_STYLES.length];
				return (
					<div
						className={`bsc-card${dropTargetId === (card.id || 'uncategorized') ? ' bsc-drop-target' : ''}`}
						key={card.id || 'uncategorized'}
						style={{ background: zone.gradient }}
						onDragOver={(e) => { if (draggingId) { e.preventDefault(); setDropTargetId(card.id || 'uncategorized'); } }}
						onDragLeave={() => setDropTargetId(null)}
						onDrop={(e) => {
							e.preventDefault();
							setDropTargetId(null);
							if (draggingId && onUpdateIndicator) {
								onUpdateIndicator(draggingId, { indicatorCategoryId: card.id });
							}
							setDraggingId(null);
						}}
					>
						<div className="bsc-card-title">
							{/* Pastille pleine à liseré blanc, comme les labels de zones
							    de la viz risques. */}
							<span className="bsc-zone-dot" style={{ backgroundColor: zone.dot }} />
							<span className="bsc-title-text">{card.name}</span>
							<span className="bsc-count">{card.items.length}</span>
							{onCreate && (
								<Tooltip title={t('models.indicator.action.add_x')}>
									<span
										className="bsc-add"
										data-testid={`bsc-add-${card.id || 'uncategorized'}`}
										onClick={(e) => {
											e.stopPropagation();
											onCreate(card.id);
										}}
									>
										<FontAwesomeIcon icon={faPlus} />
									</span>
								</Tooltip>
							)}
						</div>
						<div className="bsc-card-body">
							{card.items.length === 0 && (
								<div className="bsc-empty">
									{t('models.indicator.message.no_x')}
								</div>
							)}
							{sortBy(card.items, (e) => e.alt.name).map(({ raw, alt }) => {
								const baseline = scores[raw.id]?.['baseline'];
								const current = scores[raw.id]?.[scenario?.id || 'baseline'];
								const isBest =
									current === maxScore(raw.id) && current !== minScore(raw.id);
								return (
									<div
										className={`bsc-indicator${draggingId === raw.id ? ' bsc-dragging' : ''}`}
										key={raw.id}
										data-testid={`bsc-indicator-${raw.id}`}
										draggable={!!onUpdateIndicator}
										onDragStart={() => setDraggingId(raw.id)}
										onDragEnd={() => { setDraggingId(null); setDropTargetId(null); }}
										onClick={() =>
											raw.id === selection?.id
												? deselect && deselect()
												: select && select(raw)
										}
										onDoubleClick={() => onEdit && onEdit(raw)}
									>
										<div className="bsc-ind-label">
											<span
												className={
													'bsc-name' + (raw.id === selection?.id ? ' selected' : '')
												}
												title={alt.name}
											>
												{alt.name}
											</span>
											{/* Coupe : meilleure valeur across scénarios. */}
											{isBest && (
												<Tooltip title={t('models.scenario.messages.best_evaluation')}>
													<FontAwesomeIcon className="trophy" icon={faTrophy} />
												</Tooltip>
											)}
											{/* Flèche d'évolution vs baseline — quand un scénario est actif. */}
											{scenario && current > baseline && (
												<span className="rm45">
													<Tooltip title="Better than baseline">
														<FontAwesomeIcon icon={faArrowRight} />
													</Tooltip>
												</span>
											)}
											{scenario && current < baseline && (
												<span className="rp45">
													<Tooltip title="Worst than baseline">
														<FontAwesomeIcon icon={faArrowRight} />
													</Tooltip>
												</span>
											)}
											{scenario && current === baseline && (
												<span className="r0">
													<Tooltip title="Same as baseline">
														<FontAwesomeIcon icon={faEquals} />
													</Tooltip>
												</span>
											)}
										</div>
										{/* Progress bar fine et arrondie : remplissage 0 -> valeur,
										    couleur pleine de l'indicateur (gauge). En scénario, un
										    tick blanc marque la baseline. */}
										<div className="bsc-axis">
											<span className="bsc-track" />
											<span
												className="bsc-fill"
												style={{
													width: `${current}%`,
													backgroundColor: getScoreColor(current)
												}}
											/>
											{/* Un tick par AUTRE scénario (baseline incluse) : position
											    = sa valeur, tooltip nom + valeur au survol (zone de
											    survol élargie de 3px). */}
											{allScenarios
												.filter(
													(sc) => (sc?.id || 'baseline') !== (scenario?.id || 'baseline')
												)
												.map((sc) => {
													const v = scores[raw.id]?.[sc?.id || 'baseline'];
													if (v === undefined) return null;
													return (
														<Tooltip
															key={sc?.id || 'baseline'}
															title={sc?.name || t('models.scenario.baseline')}
														>
															<span
																className="bsc-tick-hit"
																onClick={(e) => e.stopPropagation()}
																style={{ left: `${v}%` }}
															>
																<span className="bsc-scenario-tick" />
															</span>
														</Tooltip>
													);
												})}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default IndicatorsBSC;
